import * as joint from '@joint/core';

export interface FlowEditorOptions {
    container: HTMLElement;
    width: number;
    height: number;
    dotNetHelper: any;
    futureStateMode: boolean;
    // The entity action bar calls back into the host (OnEntityAction), so only hosts that
    // implement it enable it; everywhere else the buttons would reject on click.
    showEntityActions: boolean;
}

export interface FlowPreviewOptions {
    container: HTMLElement;
    graphJson: string;
}

export enum ShapeType {
    CorporationCFC = 1,
    Branch = 2,
    Partnership = 3,
    HybridEntity = 4,
    ReverseHybridEntity = 5,
    DRE = 6,
    Group = 7,
    ThirdParty = 8,
}

export enum TransactionType {
    ServiceFee = 0,
    Royalty = 1,
    IPTransfer = 2,
    Loan = 3,
    Dividend = 5,
    Capital = 6,
    Guarantee = 7,
}

// Must be kept in sync with FlowType.cs (in the Core project)
export enum FlowType {
    Physical,
    Legal,
    Payment,
    Service,
    IPTransfer,
    IPLicense,
    Agreement,
    Miscellaneous,
    Ownership,
}

export enum Jurisdiction {
    US = 1,
    NonUS = 2,
    Mixed = 3,
    ThirdParty = 4
}

export enum PaymentType {
    Royalty = 0,
    ServiceFee = 1,
    BuySell = 2,
}

export enum ConnectorStyle {
    Sharp = 'sharp',
    Rounded = 'rounded',
    Curved = 'curved',
}

// Interfaces for colorable capabilities
export interface IFillColorable extends joint.dia.Element {
    setColor(color: string): void;
    getColor(): string;
}

export interface ITextColorable extends joint.dia.Element {
    setFontColor(color: string): void;
    getFontColor(): string;
}

export interface IBorderColorable extends joint.dia.Element {
    setBorderColor(color: string): void;
    getBorderColor(): string;
}

export interface IShapeColorable extends joint.dia.Element {
    setShapeColor(color: string): void;
    getShapeColor(): string;
}

// Interface for links that support connector styles
export interface IConnectorStyleable {
    setConnectorStyle(style: ConnectorStyle): void;
    getConnectorStyle(): ConnectorStyle;
}

// Type guards
export function isFillColorable(obj: unknown): obj is IFillColorable {
    return !!obj && typeof (obj as any).setColor === 'function';
}

export function isTextColorable(obj: unknown): obj is ITextColorable {
    return !!obj && typeof (obj as any).setFontColor === 'function';
}

export function isBorderColorable(obj: unknown): obj is IBorderColorable {
    return !!obj && typeof (obj as any).setBorderColor === 'function';
}

export function isShapeColorable(obj: unknown): obj is IShapeColorable {
    return !!obj && typeof (obj as any).setShapeColor === 'function';
}

export function isConnectorStyleable(obj: unknown): obj is IConnectorStyleable {
    return !!obj && typeof (obj as any).setConnectorStyle === 'function';
}

export type LinkTypeColor = {
    type: string;
    color: string;
};

export type FlowTypeInfo = {
    flowType: FlowType;
    linkClassName: string;
    color: string;
    isDashed: boolean;
    isThin: boolean;
    hasArrowhead: boolean;
};

export type IconName = typeof AvailableIconNames[number];

export const AvailableIconNames = [
    'Key Personnel',
    'Production Facility',
    'Truck',
    'Rail',
    'Boat',
    'Plane',
    'Distribution Center',
    'Title Transfer',
    'Shipping Port',
] as const;

export interface EntityNodeOptions {
    jurisdiction: Jurisdiction;
    shape: ShapeType;
    label: string;
    entityIds: string[];
    isNewEntity: boolean;
    countryCode?: string;
    country?: string;
    diagramType?: string;
    entityId?: string;
}

export interface MenuOption {
    text: string;
    action: () => void | any;
    subMenu?: MenuOption[];
}

export enum ToolMode {
    AddFlow,
    AddTransaction,
    DrawRectangle,
    DrawTextBox,
    DrawCallout,
    DrawIcon,
    DrawLine,
    MoveRevenueExpense,
    MoveFunction,
}

export const toolModesWithTargetSelection = [ToolMode.AddFlow, ToolMode.AddTransaction, ToolMode.MoveRevenueExpense, ToolMode.MoveFunction];

export type ResizeDirection = 'left' | 'right' | 'top' | 'bottom' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
export type AlignmentDirection = 'left' | 'right' | 'top' | 'bottom';

export type Rect = { x: number; y: number; width: number; height: number; };
export type LayoutFormation = 'vertical' | 'horizontal' | 'grid';
export type LayoutItem = { element: joint.dia.Element; bbox: Rect; offsetX: number; offsetY: number; };
export type DragToolMode = ToolMode.AddFlow | ToolMode.AddTransaction | ToolMode.MoveRevenueExpense;

// Every tool that picks a source and a target. Move Function is click-based rather than
// drag-based, but its endpoints are validated by the same predicate as the drag tools.
export type TargetSelectionToolMode = DragToolMode | ToolMode.MoveFunction;

export type CalloutPoint = { x: number; y: number; };
export type CalloutSegment = { start: CalloutPoint; end: CalloutPoint; delta: CalloutPoint; length: number; };
